import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Yii } from "../../../../interfaces/Yii";
import { BookingType, PriceInBookingType, SavingType } from "@afidium/ts-facade";
import { OfferType } from "@afidium/ts-facade/model/offerType";
import { PriceEnvelopeInBookingType } from "@afidium/ts-facade/model/priceEnvelopeInBookingType";
import { DifferenceType } from "@afidium/ts-facade/model/differenceType";
import { wenji } from "libraries/interfaces/IWenji";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { DatePipe, Location } from "@angular/common";
import { Subscription } from "rxjs";
import { faExclamationTriangle, faInfoCircle, faMoneyBill, IconDefinition } from "@fortawesome/free-solid-svg-icons";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { NotificationType } from "libraries/interfaces/INotification";
import { ILabels } from "libraries/interfaces/ILabels";
import { ConfirmDialogComponent } from "libraries/component/dialogs/confirm-dialog/confirm-dialog.component";
import { filter } from "rxjs/operators";
import { EHosts } from "libraries/enums/EHosts";
import { FareOptimisationType } from "@afidium/ts-facade/model/fareOptimisationType";
import { FareOptimisationOfferType } from "@afidium/ts-facade/model/fareOptimisationOfferType";
import { FareActionType, FareMethodType } from "./fare-method-type";
import {
    FareOptimisationNavigationService
} from "libraries/component/booking-information/components/booking-fare-optimisation-card/components/fare-optimisation-navigation/fare-optimisation-navigation.service";
import { FareOptimisationService } from "libraries/service/fare-optimisation/fare-optimisation.service";
import { PriceItemType } from "@afidium/ts-facade/model/priceItemType";
import {
    FareOptimisationRejectFormComponent
} from "libraries/component/booking-information/components/booking-fare-optimisation-card/components/fare-optimisation-reject-form/fare-optimisation-reject-form.component";
import { FareOptimisationResourceService } from "libraries/service/resources/fare-optimisation/fare-optimisation-resource.service";

interface IFareOptimisationRow {
    optimisationOffersWithDifferences: Array<{
        offer: OfferType,
        differenceArray: DifferenceType[]
    }>;
    bookingPrice: PriceEnvelopeInBookingType;
}

class Wording {
    public readonly title: string = Yii.t('wui-sasha-js', 'Optimisation tarifaire');
    public readonly assistant: string = Yii.t('wui-sasha-js', 'Assistant');
    public readonly back: string = Yii.t('wui-sasha-js', 'Retour');
    public readonly accept: string = Yii.t('wui-sasha-js', 'Accepter');
    public readonly acceptSameBooking: string = Yii.t('wui-sasha-js', 'Accepter (même dossier)');
    public readonly acceptNewBooking: string = Yii.t('wui-sasha-js', 'Accepter (nouveau dossier)');
    public readonly reject: string = Yii.t('wui-sasha-js', 'Rejeter');
    public readonly loadingOptimisationAcceptance: string = Yii.t('wui-sasha-js', "Acceptation de l'optimisation en cours ...");
    public readonly optimisationError: string = Yii.t('wui-sasha-js', "Optimisation impossible,");
    public readonly optimisationErrorReject: string = Yii.t('wui-sasha-js', "Rejet de l'optimisation impossible,");
    public readonly optimisationRealize: string = Yii.t('wui-sasha-js', "Optimisation réalisée,");
    public readonly optimisationReject: string = Yii.t('wui-sasha-js', "Optimisation rejetée,");
    public readonly realBenefit: string = Yii.t('wui-sasha-js', ", Gain réel :");
    public readonly updateBooking: string = Yii.t('wui-sasha-js', "Actualiser le dossier");
    public readonly loadingOptimisationReject: string = Yii.t('wui-sasha-js', "Rejet de l'optimisation en cours ...");
}

@Component({
    selector: 'booking-fare-optimisation-card',
    template: `
        <card id="fareOptimisationCard">
            <div class="card-header-content">
                <div class="title">
                    <fa-icon [icon]="iconMoney"></fa-icon>
                    {{ wording.title }}
                </div>
                <div class="btn-container">
                    <btn *ngIf="offerId" type="actionDo"
                         [value]="wording.back"
                         (onClick)="showList()">
                    </btn>

                    <btn *ngIf="offerId && (actionForFareOptimisation || !isAcceptButtonBookingVisible)" type="actionPrincipal"
                         class="accept"
                         [value]="wording.accept"
                         (onClick)="acceptFareOptimisation(fareMethodEnum.Update)">
                    </btn>

                    <btn *ngIf="offerId && isAcceptButtonBookingVisible" type="actionPrincipal"
                         class="accept"
                         [value]="wording.acceptNewBooking"
                         (onClick)="acceptFareOptimisation(fareMethodEnum.Duplication)">
                    </btn>
                    <btn *ngIf="offerId && isAcceptButtonBookingVisible" type="actionPrincipal"
                         class="accept"
                         [value]="wording.acceptSameBooking"
                         (onClick)="acceptFareOptimisation(fareMethodEnum.Update)">
                    </btn>
                    <btn *ngIf="offerId" type="warning"
                         class="reject"
                         [value]="wording.reject"
                         (onClick)="openRejectForm()">
                    </btn>
                    <btn *ngIf="assistantUrl"
                         class="btn-right-align"
                         (onClick)="openAssistantUrl()"
                         label="{{ wording.assistant }}"
                         type="actionHeaderLink">
                    </btn>
                </div>
            </div>
            <div class="card-body-content">
                <div class="notification-wrapper">
                    <loader *ngIf="loadingOptimisationReject"
                            class="reject-loader"
                            [message]="wording.loadingOptimisationReject"></loader>
                    <loader *ngIf="loadingOptimisationAccept"
                            class="accept-loader"
                            [message]="wording.loadingOptimisationAcceptance"></loader>
                    <notification *ngIf="isOptimisationError"
                                  class="notification card-body-content"
                                  [type]="notificationType.Error">
                        <fa-icon [icon]="iconExclamationTriangle" class="notification-icon"></fa-icon>
                        {{ wording.optimisationError }} {{ errorOptimisationMessage }}
                    </notification>
                    <notification *ngIf="isOptimisationAccept && isNotificationVisible"
                                  class="notification card-body-content"
                                  [type]="notificationType.Info">
                        <div class="content-wrapper">
                            <div>
                                <fa-icon [icon]="iconInfo" class="notification-icon"></fa-icon>
                                {{ wording.optimisationRealize }} {{ currentDate }} {{ wording.realBenefit }} <span
                                    [innerHTML]="currencyValue | price: currencyCode"></span>
                            </div>
                            <div>
                                <btn label="{{ wording.updateBooking }}"
                                     type="actionDo"
                                     (onClick)="onUpdateBooking()">
                                </btn>
                            </div>
                        </div>
                    </notification>
                    <notification *ngIf="isOptimisationRejectError"
                                  class="notification card-body-content"
                                  [type]="notificationType.Error">
                        <fa-icon [icon]="iconExclamationTriangle" class="notification-icon"></fa-icon>
                        {{ wording.optimisationErrorReject }} {{ errorOptimisationMessageReject }}
                    </notification>
                    <notification *ngIf="isOptimisationRejectComplete && isNotificationVisible"
                                  class="notification card-body-content"
                                  [type]="notificationType.Info">
                        <div class="content-wrapper">
                            <div>
                                <fa-icon [icon]="iconInfo" class="notification-icon"></fa-icon>
                                {{ wording.optimisationReject}} {{ currentDate }} {{ otherReason }}
                            </div>
                            <div>
                                <btn label="{{ wording.updateBooking }}"
                                     type="actionDo"
                                     (onClick)="onUpdateBooking()">
                                </btn>
                            </div>
                        </div>
                    </notification>
                </div>
                <ng-container *ngIf="offerId; else noOffer">
                    <fare-optimisation-navigation *ngIf="fareOptimisation"
                                                  [fareOptimisation]="fareOptimisation"
                                                  [selectedOfferId]="offerId"
                                                  [prices]="prices"
                                                  [bookingSyscode]="bookingSyscode"
                                                  [booking]="booking"
                    ></fare-optimisation-navigation>
                    <fare-optimisation-detail [offerId]="offerId"
                                              [bookingSyscode]="bookingSyscode"
                                              [booking]="booking"
                    ></fare-optimisation-detail>
                </ng-container>
                <ng-template #noOffer>
                    <fare-optimisation-list [fareOptimisation]="fareOptimisation"
                                            [booking]="booking"
                                            [bookingSyscode]="bookingSyscode"
                    ></fare-optimisation-list>
                </ng-template>

            </div>
        </card>
    `,
    styles: [`
        .card-body-content {
            padding: 0.75rem 0.75rem 0;
        }

        .content-wrapper {
            display: flex;
            justify-content: space-between;
            align-items: center;
        }

        .notification {
            width: 100%;
            padding: 0;
        }
    `]
})
export class BookingFareOptimisationCardComponent implements OnInit, OnDestroy {
    @Input()
    public fareOptimisation: FareOptimisationType;

    @Input()
    public bookingSyscode: string;

    @Input()
    public prices: PriceInBookingType[];

    @Input()
    public booking: BookingType;

    @Output()
    public readonly onUpdate: EventEmitter<void> = new EventEmitter();

    public readonly notificationType: typeof NotificationType = NotificationType;

    public offerId?: string;

    public wording: Wording = new Wording();

    public assistantUrl: string;

    public offer: string;

    public iconMoney: IconDefinition = faMoneyBill;

    public loadingOptimisationAccept: boolean = false;

    public loadingOptimisationReject: boolean = false;

    public isOptimisationRejectError: boolean;

    public isOptimisationRejectComplete: boolean = false;

    public isOptimisationAccept: boolean = false;

    public isOptimisationError: boolean = false;

    public iconExclamationTriangle: IconDefinition = faExclamationTriangle;

    public iconInfo: IconDefinition = faInfoCircle;

    public currentDate: string;

    public errorOptimisationMessage: string;

    public errorOptimisationMessageReject: string;

    public currencyValue: number;

    public currencyCode: string;

    public isNotificationVisible: boolean = true;

    public priceQuotationType: string;

    public priceStoredFareType: string;

    public otherReason: string;

    public isAcceptButtonBookingVisible: boolean = false;

    public actionForFareOptimisation: boolean = false;

    public fareMethodEnum: typeof FareMethodType = FareMethodType;

    public fareActionEnum: typeof FareActionType = FareActionType;

    private subscription: Subscription = new Subscription();

    private readonly DIALOG_UPDATE_WORDING: ILabels = {
        title: Yii.t('wui-sasha-js', 'Mise à jour du dossier'),
        question: Yii.t('wui-sasha-js', 'Voulez-vous mettre à jour le dossier ?'),
        cancel: Yii.t('wui-sasha-js', 'Abandonner'),
        accept: Yii.t('wui-sasha-js', 'Mettre à jour le dossier sélectionné'),
    };


    constructor(
        private readonly dialog: MatDialog,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly location: Location,
        private readonly fareOptimisationNavigationService: FareOptimisationNavigationService,
        private readonly datePipe: DatePipe,
        private readonly fareOptimisationService: FareOptimisationService,
        private readonly fareOptimisationResourceService: FareOptimisationResourceService,
    ) {
    }

    public ngOnInit(): void {
        const fareOptimisationList: FareOptimisationOfferType[] = this.fareOptimisation?.fareOptimisationOfferToBeProcessed?.fareOptimisationOfferArray ?? [];
        if (fareOptimisationList?.length === 1 &&
            fareOptimisationList[0].actionForFareOptimisation === "SPLIT") {
            const travellerSignature: string = this.getTravellerThanksToSavingArray(fareOptimisationList[0].savingArray[0]);
            this.fareOptimisationService.filterPaxOnTarificationPrice(fareOptimisationList[0].price.cost, travellerSignature);
        }
        this.iconMoney = faMoneyBill;
        this.subscription.add(
            this.route.params
                .subscribe(
                    (params: Params): void => {
                        this.offerId = params.offerId;
                        this.setAssistantUrl();
                    }
                )
        );

        this.subscription.add(
            this.fareOptimisationNavigationService.navigationEvent.subscribe((offerId: string): void => {
                this.offerId = offerId;
                this.setAssistantUrl();
                this.processFareOptimisationOffer(offerId);
            })
        );

        this.isAcceptButtonBookingVisible = this.booking.host?.code === EHosts.TRAVELPORT;
        this.currentDate = this.datePipe.transform(new Date(), 'dd/MM/yyyy');
        this.processFareOptimisationOffer(this.offerId);
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public openAssistantUrl(): void {
        window.open(
            this.assistantUrl,
            "_blank"
        );
    }

    public showList(): void {
        this.offerId = undefined;
        this.removeUrlOfferId();
    }

    public removeUrlOfferId(): void {
        const currentUrl: string = this.router.url;

        const newUrl: string = currentUrl.split(`/${this.offerId}`).join();
        this.location.go(newUrl);
    }

    public openRejectForm(): void {
        const dialogRef: MatDialogRef<FareOptimisationRejectFormComponent> = this.dialog.open(FareOptimisationRejectFormComponent, {
            data: {
                syscode: this.booking?.syscode?.value,
                host: this.booking?.host?.code as EHosts,
                priceQuotationTypeReference: this.priceQuotationType,
                priceStoredFareTypeReference: this.priceStoredFareType,
            },
            panelClass: 'custom-dialog-container'
        });

        dialogRef.afterClosed().subscribe((request): void => {
            if (request) {
                this.loadingOptimisationReject = true;
                this.fareOptimisationResourceService.rejectFareOptimisation(request)
                    .subscribe({
                        error: (error: Error): void => {
                            this.errorOptimisationMessageReject = error.message;
                            this.isOptimisationRejectError = true;
                            this.loadingOptimisationReject = false;
                        },
                        next: (): void => {
                            this.loadingOptimisationReject = false;
                            this.isOptimisationRejectComplete = true;
                            this.otherReason = request.otherReason;
                        }
                    });
            }
        });
    }

    public processFareOptimisationOffer(offerId: string): void {
        this.fareOptimisation.fareOptimisationOfferToBeProcessed.fareOptimisationOfferArray.find((offer: FareOptimisationOfferType): void => {
            if (this.offerId === offer.offer?.dbId.toString()) {
                this.currencyValue = offer.savingArray[0].cost.totalArray[0].currencyArray[0].value;
                this.currencyCode = offer.savingArray[0].cost.totalArray[0].currencyArray[0].currencyCode;
                this.priceQuotationType = offer.offer?.signature;
                this.priceStoredFareType = offer.price?.cost?.signature;
                this.actionForFareOptimisation = offer.actionForFareOptimisation?.toUpperCase() === this.fareActionEnum.Split && this.booking.host?.code === EHosts.AMADEUS;
            }
        });
    }

    public acceptFareOptimisation(method: FareMethodType): void {
        this.loadingOptimisationAccept = true;
        this.fareOptimisationResourceService.acceptOptimisation(this.booking?.syscode?.value, this.booking?.host?.code as EHosts, method, this.priceQuotationType, this.priceStoredFareType)
            .subscribe({
                error: (error: any): any => {
                    this.errorOptimisationMessage = error.message;
                    this.isOptimisationError = true;
                    this.loadingOptimisationAccept = false;
                },
                next: (): any => {
                    this.loadingOptimisationAccept = false;
                    this.isOptimisationAccept = true;
                }
            });
    }

    public onUpdateBooking(): void {
        this.showConfirmDialog(this.DIALOG_UPDATE_WORDING, () => {
            this.onUpdate.emit();
        });
        this.isNotificationVisible = false;
    }

    /**
     * Shows a confirm dialog.
     * @param labels labels displayed
     * @param next what to do when the confirm dialog is validated
     */
    private showConfirmDialog(labels: ILabels, next: () => void): void {
        const confirmDialog: MatDialogRef<ConfirmDialogComponent> = this.dialog.open(ConfirmDialogComponent, {
            width: '650px',
            data: labels,
        });

        confirmDialog
            .afterClosed()
            .pipe(
                filter((value: string) => value === 'process'),
            ).subscribe(next);
    }

    private setAssistantUrl(): void {
        const assistantUrlElements: (string | undefined)[] = [
            wenji?.air?.airInstanceUrl,
            "wui_airFareOpt",
            "assistant",
            "booking",
            this.booking?.code,
            "creation_date",
            this.booking?.whenCreated?.value,
            "host",
            this.booking?.owner?.agency?.owner,
            "interface",
            this.booking?.host?.code,
            "agency",
            this.booking?.owner?.agency?.code
        ];

        if (false === assistantUrlElements.includes(undefined)) {
            this.assistantUrl = assistantUrlElements.join("/");
        }
    }

    private getTravellerThanksToSavingArray(saving: SavingType): string {
        const totalWithTraveller: PriceItemType = (saving.cost?.totalArray || []).find((total: PriceItemType) => total?.totalPerTravellerArray?.length);
        // Getting first traveller as only one traveller exists on SPLIT
        return totalWithTraveller?.totalPerTravellerArray[0].traveller?.signature;
    }
}
